import { useState } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { useGetCoupons, useCreateCoupon, useUpdateCoupon, useDeleteCoupon } from '../backend/hooks';
import { PageHeaderSkeleton, TableSkeleton } from '../common/skeleton';

const emptyForm = {
  code: '',
  discount_type: 'percent',
  discount_value: '',
  min_order_amount: '',
  max_discount_amount: '',
  usage_limit: '',
  expires_at: '',
  is_active: true,
};

const money = (value) => 'Rs ' + Number(value || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const describeDiscount = (coupon) =>
  coupon.discount_type === 'percent'
    ? `${Number(coupon.discount_value)}% off${coupon.max_discount_amount ? ` (max ${money(coupon.max_discount_amount)})` : ''}`
    : `${money(coupon.discount_value)} off`;

const couponStatus = (coupon) => {
  if (!coupon.is_active) return { label: 'Inactive', className: 'bg-slate-100 text-slate-600' };
  if (coupon.expires_at && new Date(coupon.expires_at).getTime() < Date.now())
    return { label: 'Expired', className: 'bg-red-100 text-red-700' };
  if (coupon.usage_limit !== null && coupon.used_count >= coupon.usage_limit)
    return { label: 'Used up', className: 'bg-amber-100 text-amber-800' };
  return { label: 'Active', className: 'bg-green-100 text-green-800' };
};

const randomCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = 'KX';
  for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
};

export default function Coupons() {
  const { data, isLoading, error } = useGetCoupons();
  const { mutate: createCoupon, isPending: isCreating } = useCreateCoupon();
  const { mutate: updateCoupon } = useUpdateCoupon();
  const { mutate: deleteCoupon } = useDeleteCoupon();
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : name === 'code' ? value.toUpperCase().replace(/\s/g, '') : value,
    }));
    setFormError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!/^[A-Z0-9_-]{3,50}$/.test(form.code)) {
      setFormError('Code must be 3-50 characters: letters, numbers, - or _');
      return;
    }
    const value = Number(form.discount_value);
    if (!Number.isFinite(value) || value <= 0) {
      setFormError('Discount value must be greater than 0');
      return;
    }
    if (form.discount_type === 'percent' && value > 100) {
      setFormError('A percentage discount cannot exceed 100');
      return;
    }

    createCoupon(
      {
        ...form,
        discount_value: value,
        // the date input gives a day; the coupon stays valid through that whole day
        expires_at: form.expires_at ? new Date(`${form.expires_at}T23:59:59`).toISOString() : null,
      },
      {
        onSuccess: () => {
          toast.success(`Coupon ${form.code} created`);
          setForm(emptyForm);
        },
        onError: (err) => setFormError(err.message),
      }
    );
  };

  const toggleActive = (coupon) => {
    updateCoupon(
      { id: coupon.id, data: { is_active: !coupon.is_active } },
      {
        onSuccess: () => toast.success(`${coupon.code} ${coupon.is_active ? 'deactivated' : 'activated'}`),
        onError: (err) => toast.error(err.message),
      }
    );
  };

  const handleDelete = (coupon) => {
    Swal.fire({
      title: 'Delete coupon?',
      text: `"${coupon.code}" will stop working immediately. Past orders keep their discount.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it',
    }).then((result) => {
      if (!result.isConfirmed) return;
      deleteCoupon(coupon.id, {
        onSuccess: () => toast.success('Coupon deleted'),
        onError: (err) => toast.error(err.message),
      });
    });
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code).then(() => toast.info(`${code} copied`)).catch(() => {});
  };

  const inputClass = 'w-full bg-surface-container-highest border-none rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary transition-all';
  const labelClass = 'block text-sm font-semibold text-on-surface-variant mb-2';

  if (isLoading) {
    return (
      <main className="p-3 space-y-8">
        <PageHeaderSkeleton />
        <TableSkeleton columns={6} rows={5} />
      </main>
    );
  }

  const coupons = data?.coupons || [];

  return (
    <div className="space-y-8 p-3">
      <div>
        <h1 className="text-3xl font-extrabold text-on-surface tracking-tight">Coupons</h1>
        <p className="text-on-surface-variant mt-2">Create discount codes customers can apply at checkout.</p>
      </div>

      <div className="bg-surface-container-lowest rounded-2xl shadow-sm border border-outline-variant/15 overflow-hidden">
        <div className="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-low">
          <h2 className="text-lg font-bold text-on-surface">Create Coupon</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className={labelClass}>Coupon Code <span className="text-error">*</span></label>
              <div className="flex gap-2">
                <input name="code" value={form.code} onChange={handleChange} className={`${inputClass} font-mono tracking-wider`} placeholder="e.g. SAVE10" />
                <button type="button" onClick={() => setForm((p) => ({ ...p, code: randomCode() }))} className="shrink-0 px-3 rounded-xl bg-surface-container-high hover:bg-surface-container-highest text-primary" title="Generate a random code">
                  <span className="material-symbols-outlined">casino</span>
                </button>
              </div>
            </div>
            <div>
              <label className={labelClass}>Discount Type</label>
              <select name="discount_type" value={form.discount_type} onChange={handleChange} className={inputClass}>
                <option value="percent">Percentage (%)</option>
                <option value="fixed">Fixed amount (Rs)</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>
                {form.discount_type === 'percent' ? 'Percent Off' : 'Amount Off (Rs)'} <span className="text-error">*</span>
              </label>
              <input name="discount_value" type="number" step="0.01" min="0" value={form.discount_value} onChange={handleChange} className={inputClass} placeholder={form.discount_type === 'percent' ? '10' : '500'} />
            </div>
            <div>
              <label className={labelClass}>Minimum Order (Rs)</label>
              <input name="min_order_amount" type="number" step="0.01" min="0" value={form.min_order_amount} onChange={handleChange} className={inputClass} placeholder="Optional" />
            </div>
            {form.discount_type === 'percent' && (
              <div>
                <label className={labelClass}>Maximum Discount (Rs)</label>
                <input name="max_discount_amount" type="number" step="0.01" min="0" value={form.max_discount_amount} onChange={handleChange} className={inputClass} placeholder="Optional cap" />
              </div>
            )}
            <div>
              <label className={labelClass}>Usage Limit</label>
              <input name="usage_limit" type="number" step="1" min="1" value={form.usage_limit} onChange={handleChange} className={inputClass} placeholder="Unlimited" />
            </div>
            <div>
              <label className={labelClass}>Expires On</label>
              <input name="expires_at" type="date" value={form.expires_at} onChange={handleChange} className={inputClass} />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-3 cursor-pointer pb-3">
                <input name="is_active" type="checkbox" checked={form.is_active} onChange={handleChange} className="rounded text-primary focus:ring-primary w-5 h-5" />
                <span className="text-sm font-semibold text-on-surface">Active immediately</span>
              </label>
            </div>
          </div>
          {formError && <p className="text-error text-sm">{formError}</p>}
          <div className="pt-4 border-t border-outline-variant/10 flex justify-end">
            <button type="submit" disabled={isCreating} className="px-6 py-3 bg-primary text-on-primary rounded-xl font-bold shadow-md hover:opacity-90 transition-all disabled:opacity-50 flex items-center gap-2">
              <span className="material-symbols-outlined text-lg">add_circle</span>
              {isCreating ? 'Creating...' : 'Create Coupon'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-surface-container-lowest rounded-2xl shadow-sm border border-outline-variant/15 overflow-hidden">
        <div className="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-low flex justify-between items-center">
          <h2 className="text-lg font-bold text-on-surface">All Coupons</h2>
          <span className="text-xs font-bold text-on-surface-variant">{coupons.length} total</span>
        </div>
        {error ? (
          <p className="p-6 text-error">Error: {error.message}</p>
        ) : coupons.length === 0 ? (
          <div className="p-12 text-center text-on-surface-variant">
            <span className="material-symbols-outlined text-5xl text-slate-300">sell</span>
            <p className="mt-2">No coupons yet. Create your first one above.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-surface-container-low text-xs font-bold text-on-surface-variant uppercase tracking-widest">
                  <th className="px-6 py-4">Code</th>
                  <th className="px-6 py-4">Discount</th>
                  <th className="px-6 py-4">Min Order</th>
                  <th className="px-6 py-4">Used</th>
                  <th className="px-6 py-4">Expires</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-container-high">
                {coupons.map((coupon) => {
                  const status = couponStatus(coupon);
                  return (
                    <tr key={coupon.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <button onClick={() => copyCode(coupon.code)} className="font-mono font-bold tracking-wider text-primary bg-primary/5 border border-dashed border-primary/30 rounded-lg px-3 py-1 hover:bg-primary/10 transition-colors" title="Copy code">
                          {coupon.code}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold whitespace-nowrap">{describeDiscount(coupon)}</td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap">{coupon.min_order_amount ? money(coupon.min_order_amount) : '—'}</td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap">{coupon.used_count}{coupon.usage_limit ? ` / ${coupon.usage_limit}` : ''}</td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap">{coupon.expires_at ? new Date(coupon.expires_at).toLocaleDateString() : 'Never'}</td>
                      <td className="px-6 py-4">
                        <span className={`text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${status.className}`}>{status.label}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => toggleActive(coupon)} className="p-2 rounded-lg text-on-surface-variant hover:bg-surface-container-high transition-colors" title={coupon.is_active ? 'Deactivate' : 'Activate'}>
                            <span className="material-symbols-outlined">{coupon.is_active ? 'toggle_on' : 'toggle_off'}</span>
                          </button>
                          <button onClick={() => handleDelete(coupon)} className="p-2 rounded-lg text-error hover:bg-error/10 transition-colors" title="Delete">
                            <span className="material-symbols-outlined">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
